import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getMesas, buscarCliente } from '../api/client'
import { useUser } from '../App'
import NavBar from '../components/NavBar'
import BalcaoPDV from '../components/BalcaoPDV'

const statusColors: Record<string, string> = {
  livre: 'bg-green-500',
  ocupada: 'bg-red-600',
  reservada: 'bg-orange-500',
}

/**
 * MesasPage — D-114 (2026-05-17): layout split em desktop.
 *   - Esquerda: grade de mesas (50 mesas).
 *   - Direita: PDV Balcão SEMPRE visível, mas só pra perfis caixa/admin.
 *     Atendente/seguranca/copa vêem só a grade (largura total).
 *   - Mobile: tabs alternam Mesas / Balcão (sem split, espaço pequeno).
 *
 * Decisão arquitetural:
 *   - Restringir Balcão a caixa/admin (não atendente) — operacionalmente
 *     o caixa fica no PDV, atendente fica no salão.
 *   - Layout split desktop garante operador NÃO sair da tela pra atender
 *     cliente do balcão.
 */
export default function MesasPage() {
  const { user } = useUser()
  const [mesas, setMesas] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [busca, setBusca] = useState('')
  const [resultados, setResultados] = useState<any[] | null>(null)
  const [buscando, setBuscando] = useState(false)
  const [tabMobile, setTabMobile] = useState<'mesas' | 'balcao'>('mesas')
  const nav = useNavigate()

  // Só caixa e admin têm Balcão integrado. Atendente/seguranca/copa = só grade.
  const podeOperarBalcao = user?.perfil === 'caixa' || user?.perfil === 'admin'

  const load = () => {
    getMesas().then(m => { setMesas(m); setLoading(false) }).catch(console.error)
  }

  useEffect(() => {
    load()
    const interval = setInterval(load, 5000)
    return () => clearInterval(interval)
  }, [])

  const handleBuscar = async () => {
    if (!busca.trim()) { setResultados(null); return }
    setBuscando(true)
    try {
      const res = await buscarCliente(busca.trim())
      setResultados(res)
    } catch {
      setResultados([])
    } finally {
      setBuscando(false)
    }
  }

  if (loading) return <div className="p-4 text-center">Carregando...</div>

  // ─── Grade de mesas (sem Balcão card — agora vive no painel PDV à direita) ─
  const renderGradeMesas = () => (
    <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 gap-2">
      {mesas.map(m => {
        const parcial = m.saldo_restante > 0 && m.total_pago > 0
        return (
          <button
            key={m.id}
            onClick={() => m.ativa && nav(`/mesa/${m.id}`)}
            disabled={!m.ativa}
            className={`relative ${statusColors[m.status_visual] || 'bg-gray-300'} text-white rounded-lg p-2 h-20 flex flex-col items-center justify-center text-center ${
              !m.ativa ? 'opacity-30 cursor-not-allowed' : ''
            }`}
          >
            {parcial && (
              <span
                className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-300"
                title="Pagamento parcial"
                aria-label="Pagamento parcial"
              />
            )}
            <div className="text-xl font-bold leading-tight">{m.numero}</div>
            <div className="text-[11px] mt-0.5 min-h-[14px] tabular-nums leading-tight">
              {m.total_atual > 0 ? `R$ ${Number(m.total_atual).toFixed(2)}` : ''}
            </div>
          </button>
        )
      })}
    </div>
  )

  const renderHeaderMesas = () => (
    <>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] text-terracota/70 font-medium leading-none">Salão</p>
          <h1 className="font-serif text-2xl font-semibold text-carvao leading-tight">Mesas</h1>
        </div>
        <div className="hidden sm:flex gap-2 text-xs">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500 inline-block" /> Livre</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-600 inline-block" /> Ocupada</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500 inline-block" /> Reservada</span>
        </div>
      </div>

      {/* Client search */}
      <div className="flex gap-1 mb-3">
        <input
          value={busca}
          onChange={e => setBusca(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleBuscar()}
          placeholder="Buscar por nome ou código..."
          className="border rounded p-2 text-sm flex-1"
        />
        <button onClick={handleBuscar} disabled={buscando} className="btn btn-secondary text-sm px-3">
          {buscando ? '...' : 'Buscar'}
        </button>
        {resultados !== null && (
          <button onClick={() => { setResultados(null); setBusca('') }} className="btn btn-secondary text-sm px-2">X</button>
        )}
      </div>

      {/* Search results */}
      {resultados !== null && (
        <div className="mb-4">
          {resultados.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-2">Nenhum cliente encontrado para "{busca}"</p>
          ) : (
            <div className="space-y-1">
              {resultados.map((r: any) => (
                <button
                  key={r.id}
                  onClick={() => nav(`/mesa/${r.mesa_id}`)}
                  className="w-full flex justify-between items-center p-2 border rounded text-left text-sm"
                >
                  <div>
                    <span className="font-bold">Mesa {r.mesa_numero}</span>
                    <span className="mx-2">→</span>
                    <span>{r.nome_cliente_opcional || `Cliente ${r.id}`}</span>
                    {r.codigo_cliente && <span className="ml-2 text-xs font-bold text-terracota">#{r.codigo_cliente}</span>}
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-gray-500">{r.status}</span>
                    {r.total_ativo > 0 && <span className="ml-2 font-bold text-xs">R$ {Number(r.total_ativo).toFixed(2)}</span>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )

  // ─── Atendente/seguranca/copa: só grade (largura total) ──────────────────
  if (!podeOperarBalcao) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="p-3">
          {renderHeaderMesas()}
          {renderGradeMesas()}
        </div>
      </div>
    )
  }

  // ─── Caixa/admin: split desktop, tabs mobile ─────────────────────────────
  return (
    <div className="min-h-screen bg-white">
      <NavBar />

      {/* Mobile (sm-): tabs Mesas / Balcão */}
      <div className="lg:hidden">
        <div className="flex border-b border-gray-200 bg-cream/30">
          <button
            onClick={() => setTabMobile('mesas')}
            className={`flex-1 py-3 text-sm font-medium transition ${
              tabMobile === 'mesas'
                ? 'text-terracota border-b-2 border-terracota'
                : 'text-gray-500 hover:text-carvao'
            }`}
          >
            Mesas
          </button>
          <button
            onClick={() => setTabMobile('balcao')}
            className={`flex-1 py-3 text-sm font-medium transition ${
              tabMobile === 'balcao'
                ? 'text-terracota border-b-2 border-terracota'
                : 'text-gray-500 hover:text-carvao'
            }`}
          >
            Balcão (PDV)
          </button>
        </div>
        {tabMobile === 'mesas' ? (
          <div className="p-3">
            {renderHeaderMesas()}
            {renderGradeMesas()}
          </div>
        ) : (
          <div className="h-[calc(100vh-120px)]">
            <BalcaoPDV />
          </div>
        )}
      </div>

      {/* Desktop (lg+): split */}
      <div className="hidden lg:flex lg:h-[calc(100vh-3.5rem)]">
        {/* Esquerda: mesas */}
        <div className="flex-1 min-w-0 overflow-y-auto p-3">
          {renderHeaderMesas()}
          {renderGradeMesas()}
        </div>
        {/* Direita: PDV Balcão (largura fixa) */}
        <div className="w-[380px] xl:w-[420px] shrink-0">
          <BalcaoPDV />
        </div>
      </div>
    </div>
  )
}
